<script lang="ts">
  import PageSection from '@athena-lib/Page/PageSection.svelte';
  import { Text, HeadingLarge, HeadingMedium } from '@athena-lib/Text';
  import { storyblokEditable } from '@storyblok/svelte';
  import { Column, Columns } from '@athena-lib/Columns';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import Image from '@athena-lib/Image/Image.svelte';
  import { RichText } from '../RichText';
  import type { ExpandedAuthorStoryBlok } from '$lib/server/expanders/authorExpander';

  const { blok }: { blok: ExpandedAuthorStoryBlok } = $props();
  const locale = getLocalizationContext();
  const format = Intl.DateTimeFormat(locale.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const translations = getCommonTranslations();
  const formatPostDate = (date: string | undefined) =>
    date ? format.format(new Date(date)) : undefined;
</script>

<PageSection action={storyblokEditable} actionArg={blok}>
  <Columns collapseBelow="md" space={{ sm: 'large', md: 'xxlarge' }}>
    <Column width="1/4" leftSpace="xxlarge" class="content-center text-center">
      <Stack>
        {#if blok.image}
          <StackItem class="flex justify-center">
            <Image
              src={blok.image?.filename}
              alt={blok.image?.alt ?? ''}
              imgClass="aspect-square w-32 rounded-full object-cover"
            />
          </StackItem>
        {/if}
        <StackItem class="text-center">
          <HeadingMedium>
            {blok.name}
          </HeadingMedium>
          <Text size="large">{blok.title}</Text>
        </StackItem>
      </Stack>
    </Column>

    <Column leftSpace="xlarge" width="3/4" class="flex items-center">
      <RichText text={blok.description} />
    </Column>
  </Columns>
  <hr class="mb-8 mt-8 border-2 border-st-gray-800 md:mt-24" />
  {#if blok.recentBlogPosts}
    <div class="flex flex-col gap-11">
      <HeadingLarge>{translations.articles}</HeadingLarge>
      <Grid columns={{ sm: 1, md: 2, lg: 2 }}>
        {#each blok.recentBlogPosts as post}
          <GridItem class="flex gap-5">
            <div class="content-center">
              {#if post.image}
                <Image
                  imgClass="aspect-5/7 w-24 min-w-24 object-cover md:aspect-square md:w-40 md:min-w-40"
                  src={post.image?.filename}
                  alt={post.image.alt ?? ''}
                  widthOfContainer={0.01}
                />
              {/if}
            </div>
            <div class="flex grow flex-col justify-center gap-1">
              <div class="flex content-center justify-between">
                <Text size="medium" bold color="red">
                  <a href={post.category?.url}>{post.category?.name}</a>
                </Text>
                <Text size="small" class="block md:hidden">
                  {formatPostDate(post.date)}
                </Text>
              </div>
              <Text size="xlarge" bold>
                <a href={post.articleUrl}>{post.title}</a>
              </Text>
              <Text size="medium" class="line-clamp-2">{post.preamble}</Text>
              <Text size="small" class="hidden md:block">{formatPostDate(post.date)}</Text>
            </div>
          </GridItem>
        {/each}
      </Grid>
    </div>
  {/if}
</PageSection>
